import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import styles from './InstallGuidePage.module.css';
import {
  ConfirmIllustration,
  HomeScreenIllustration,
  ShareButtonIllustration,
  ShareSheetIllustration,
} from './illustrations.js';

type Step = { illustration: ReactNode; title: string; text: string };

/**
 * iOS has no install prompt: the user must add the app by hand, so the app ships the
 * instructions with pictures (spec v2 23.1, decision D6).
 */
const IOS_STEPS: Step[] = [
  {
    illustration: <ShareButtonIllustration />,
    title: 'Mở trang này bằng Safari',
    text: 'Chạm nút Chia sẻ (hình vuông có mũi tên đi lên) ở thanh công cụ phía dưới.',
  },
  {
    illustration: <ShareSheetIllustration />,
    title: 'Chọn "Thêm vào MH chính"',
    text: 'Vuốt danh sách xuống một chút nếu chưa thấy dòng này.',
  },
  {
    illustration: <ConfirmIllustration />,
    title: 'Chạm "Thêm"',
    text: 'Nút Thêm nằm ở góc trên bên phải. Bạn có thể đổi tên hiển thị trước khi thêm.',
  },
  {
    illustration: <HomeScreenIllustration />,
    title: 'Mở app từ màn hình chính',
    text: 'App chạy toàn màn hình, không còn thanh địa chỉ — dùng như ứng dụng bình thường.',
  },
];

const ANDROID_STEPS: Step[] = [
  {
    illustration: <ShareButtonIllustration />,
    title: 'Mở trang này bằng Chrome',
    text: 'Chạm menu ba chấm ở góc trên bên phải.',
  },
  {
    illustration: <ShareSheetIllustration />,
    title: 'Chọn "Cài đặt ứng dụng"',
    text: 'Một số máy hiển thị là "Thêm vào màn hình chính". Chrome cũng có thể tự hiện thanh gợi ý cài đặt.',
  },
  {
    illustration: <ConfirmIllustration />,
    title: 'Xác nhận cài đặt',
    text: 'Chạm "Cài đặt" trong hộp thoại hiện ra.',
  },
  {
    illustration: <HomeScreenIllustration />,
    title: 'Mở app từ màn hình chính',
    text: 'App chạy toàn màn hình như ứng dụng cài từ cửa hàng.',
  },
];

export function InstallGuidePage() {
  const [platform, setPlatform] = useState<'ios' | 'android'>(() =>
    // Capability-ish default; the user can switch. No user-agent branching for behaviour.
    /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios' : 'android',
  );

  const steps = platform === 'ios' ? IOS_STEPS : ANDROID_STEPS;

  return (
    <div className={styles.page}>
      <h1>Thêm app vào màn hình chính</h1>
      <p className={styles.intro}>
        Cài xong, app mở nhanh hơn và chạy toàn màn hình. Không cần tải từ App Store hay CH Play.
      </p>

      <div className={styles.tabs} role="group" aria-label="Chọn loại điện thoại">
        <button
          type="button"
          className={styles.tab}
          aria-pressed={platform === 'ios'}
          onClick={() => setPlatform('ios')}
        >
          iPhone / iPad
        </button>
        <button
          type="button"
          className={styles.tab}
          aria-pressed={platform === 'android'}
          onClick={() => setPlatform('android')}
        >
          Android
        </button>
      </div>

      <ol className={styles.steps}>
        {steps.map((step, index) => (
          <li key={step.title} className={styles.step}>
            <div className={styles.illustration}>{step.illustration}</div>
            <div>
              <p className={styles.stepTitle}>
                Bước {index + 1}. {step.title}
              </p>
              <p className={styles.stepText}>{step.text}</p>
            </div>
          </li>
        ))}
      </ol>

      <Link className={styles.back} to="/">
        ← Quay lại
      </Link>
    </div>
  );
}
